import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import TrainerLayout from '../../components/layout/TrainerLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Users, ClipboardList, Dumbbell, Loader2, ChevronRight, Activity, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrainerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/trainer/dashboard`);
      setData(response.data);
    } catch (error) {
      toast.error('Error al cargar dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <TrainerLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </TrainerLayout>
    );
  }

  const stats = [
    { title: 'Mis Clientes', value: data?.total_clients || 0, icon: Users, color: 'text-blue-400', link: '/trainer/clients' },
    { title: 'Mis Planes', value: data?.total_plans || 0, icon: ClipboardList, color: 'text-primary', link: '/trainer/plans' },
    { title: 'Sesiones Totales', value: data?.total_sessions || 0, icon: Activity, color: 'text-green-400', link: null },
  ];

  return (
    <TrainerLayout>
      <div className="space-y-8 animate-fade-in" data-testid="trainer-dashboard">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Hola, {data?.trainer_name || user?.name}
          </h1>
          <p className="text-muted-foreground mt-1">Panel de Entrenador</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.map((stat) => (
            <div key={stat.title} onClick={() => stat.link && navigate(stat.link)} className={stat.link ? 'cursor-pointer' : ''}>
              <Card className="bg-card border-border card-hover">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">{stat.title}</p>
                      <p className="text-4xl font-bold text-foreground mt-2 tabular-nums">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-sm bg-white/5 ${stat.color}`}>
                      <stat.icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-card border-border card-hover cursor-pointer" onClick={() => navigate('/trainer/clients')}>
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Plus className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Crear Cliente</h3>
                  <p className="text-xs text-muted-foreground">A{'\u00f1'}adir nuevo cliente</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </CardContent>
          </Card>

          <Card className="bg-card border-border card-hover cursor-pointer" onClick={() => navigate('/trainer/plans')}>
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/20 rounded-lg">
                  <Plus className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Crear Plan</h3>
                  <p className="text-xs text-muted-foreground">Dise{'\u00f1'}ar nuevo plan de entrenamiento</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </CardContent>
          </Card>
        </div>

        {/* Recent Clients */}
        {data?.clients?.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-400" />
                Mis Clientes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {data.clients.slice(0, 5).map((client) => (
                <div key={client.id} className="flex items-center justify-between p-3 bg-surface rounded-sm">
                  <div>
                    <p className="font-medium text-foreground">{client.name}</p>
                    <p className="text-xs text-muted-foreground">{client.email}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/trainer/clients/${client.id}`)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </TrainerLayout>
  );
};

export default TrainerDashboard;
